import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent } from "@/shared/components/ui/card";
import {
  Shield, Zap, Globe, Lock, ArrowRight, CheckCircle2, CreditCard, Building2, BarChart3, Clock, Headphones, ChevronRight,
  Mail, MessageSquare, Phone, MapPin, HelpCircle, Loader2
} from "lucide-react";
import { contactService } from "@/shared/api/services/contactService";
import { useToast } from "@/shared/components/ui/use-toast";
import logo from "@/assets/logo.png";

const features = [
  {
    icon: Zap,
    title: "Lightning Fast",
    description: "Process transactions in milliseconds with our optimized infrastructure"
  },
  {
    icon: Shield,
    title: "Bank-Grade Security",
    description: "PCI DSS Level 1 compliant with end-to-end encryption"
  },
  {
    icon: Globe,
    title: "Global Coverage",
    description: "Accept payments from 180+ countries with local payment methods"
  },
  {
    icon: BarChart3,
    title: "Real-time Analytics",
    description: "Monitor your transactions with comprehensive dashboards"
  },
  {
    icon: Clock,
    title: "99.99% Uptime",
    description: "Enterprise-grade reliability you can count on"
  },
  {
    icon: Headphones,
    title: "24/7 Support",
    description: "Dedicated support team available around the clock"
  }
];

const stats = [
  { value: "$2B+", label: "Processed Monthly" },
  { value: "99.99%", label: "Uptime" },
  { value: "180+", label: "Countries" },
  { value: "<50ms", label: "Latency" }
];

export default function Landing() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = React.useState(false);
  const [formData, setFormData] = React.useState({
    fullName: '',
    email: '',
    message: ''
  });

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fullName || !formData.email || !formData.message) {
      toast({
        title: "Missing Information",
        description: "Please fill in all fields before sending.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      await contactService.saveMessage(formData);
      toast({
        title: "Message Sent!",
        description: "Thank you for reaching out. We'll get back to you soon.",
      });
      setFormData({ fullName: '', email: '', message: '' });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 hero-gradient">
        <div className="absolute inset-0 grid-pattern" />
        <div className="max-w-7xl mx-auto relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700/50 mb-8">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-sm text-slate-400">Now processing $2B+ monthly</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              The Payment Gateway
              <br />
              <span className="gradient-text">Built for Scale</span>
            </h1>

            <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
              Accept payments globally with enterprise-grade security, lightning-fast processing,
              and comprehensive tools for engines, resellers, and merchants.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                size="lg"
                className="bg-gradient-to-r from-blue-500 to-violet-600 hover:from-blue-600 hover:to-violet-700 text-white px-8 py-6 text-lg rounded-xl"
                onClick={() => window.location.href = 'https://agent.panel.luxkgate.com/login'}
              >
                Start Processing
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-slate-700 text-slate-300 hover:text-white hover:bg-slate-800 px-8 py-6 text-lg rounded-xl transition-colors bg-transparent"
                onClick={() => window.location.href = 'https://docs.panel.luxkgate.com/'}
              >
                View Documentation
              </Button>
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20"
          >
            {stats.map((stat, index) => (
              <div key={index} className="text-center p-6 rounded-2xl bg-slate-900/50 border border-slate-800/50">
                <div className="text-3xl md:text-4xl font-bold gradient-text mb-2">{stat.value}</div>
                <div className="text-slate-500 text-sm">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">
              Everything You Need to
              <span className="gradient-text"> Accept Payments</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Built from the ground up for high-volume payment processing with the tools and
              flexibility modern businesses demand.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card className="bg-slate-900/50 border-slate-800/50 hover:border-slate-700 transition-all duration-300 card-glow h-full">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-violet-500/20 flex items-center justify-center mb-4">
                      <feature.icon className="h-6 w-6 text-blue-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                    <p className="text-slate-400 text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* User Types Section */}
      <section className="py-20 px-4 bg-slate-900/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">
              Built for <span className="gradient-text">Every Business Model</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Whether you're an engine operator, reseller, or merchant, we have the tools you need.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Zap,
                title: "Engines",
                description: "Full payment infrastructure management with multi-tenant support, provider routing, and comprehensive analytics.",
                features: ["Multi-tenant architecture", "Provider management", "Real-time monitoring", "Custom routing rules"],
                color: "violet",
                url: "https://engine.panel.luxkgate.com/login"
              },
              {
                icon: Building2,
                title: "Resellers",
                description: "White-label solutions for partners looking to offer payment services under their own brand.",
                features: ["White-label branding", "Merchant management", "Commission tracking", "Dedicated support"],
                color: "emerald",
                url: "https://agent.panel.luxkgate.com/login"
              },
              {
                icon: CreditCard,
                title: "Merchants",
                description: "Simple integration for businesses of all sizes with powerful tools to manage your payments.",
                features: ["Easy API integration", "Multiple payment methods", "Instant settlements", "Fraud protection"],
                color: "amber",
                url: "https://agent.panel.luxkgate.com/login"
              }
            ].map((type, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card className="bg-slate-900/50 border-slate-800/50 hover:border-slate-700 transition-all duration-300 h-full">
                  <CardContent className="p-8">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${type.color === 'violet' ? 'from-violet-500/20 to-purple-500/20' :
                      type.color === 'emerald' ? 'from-emerald-500/20 to-teal-500/20' :
                        'from-amber-500/20 to-orange-500/20'
                      } flex items-center justify-center mb-6`}>
                      <type.icon className={`h-7 w-7 ${type.color === 'violet' ? 'text-violet-400' :
                        type.color === 'emerald' ? 'text-emerald-400' :
                          'text-amber-400'
                        }`} />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-3">{type.title}</h3>
                    <p className="text-slate-400 mb-6">{type.description}</p>
                    <ul className="space-y-3">
                      {type.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                          <CheckCircle2 className={`h-4 w-4 ${type.color === 'violet' ? 'text-violet-400' :
                            type.color === 'emerald' ? 'text-emerald-400' :
                              'text-amber-400'
                            }`} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button
                      className="w-full mt-6 bg-slate-800 hover:bg-slate-700 text-white"
                      onClick={() => window.location.href = `${type.url}`}
                    >
                      Get Started
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Docs / FAQ Section */}
      <section id="docs" className="py-20 px-4 bg-slate-900/30">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">
              Common <span className="gradient-text">Questions</span>
            </h2>
            <p className="text-slate-400">Everything you need to know about the product and billing.</p>
          </motion.div>

          <div className="grid gap-4">
            {[
              { q: "How fast is the integration process?", a: "Most merchants can complete our standard API integration within a single day. We provide comprehensive SDKs and code examples in multiple languages." },
              { q: "What payment methods do you support?", a: "We support major credit cards (Visa, Mastercard, Amex), Apple Pay, Google Pay, and over 150 local payment methods globally." },
              { q: "Are there any setup fees?", a: "No, there are no setup fees for our Merchant and Reseller plans. Engine integrations may require custom implementation scopes." },
              { q: "How does the white-label solution work?", a: "Our reseller platform allows you to completely rebrand the merchant dashboard, API endpoints, and checkout flows with your own domain and logo." }
            ].map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.1 }}
                className="p-6 rounded-2xl bg-slate-900/50 border border-slate-800/50 hover:border-slate-700 transition-colors"
              >
                <div className="flex gap-4">
                  <div className="mt-1">
                    <HelpCircle className="h-6 w-6 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">{faq.q}</h4>
                    <p className="text-slate-400 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid lg:grid-cols-2 gap-12 lg:gap-24"
          >
            <div>
              <h2 className="text-4xl font-bold mb-6">
                Get in <span className="gradient-text">Touch</span>
              </h2>
              <p className="text-slate-400 mb-10 text-lg">
                Have questions about our payment gateway? Our team would love to hear from you and help you find the right solution.
              </p>

              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                    <Mail className="h-6 w-6 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Email Us</h4>
                    <p className="text-slate-400 text-sm">support@luxkgate.com</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center shrink-0">
                    <Phone className="h-6 w-6 text-violet-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Call Us</h4>
                    <p className="text-slate-400 text-sm">+1 (555) 123-4567</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                    <MapPin className="h-6 w-6 text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Office</h4>
                    <p className="text-slate-400 text-sm">123 Financial District, Toronto, ON</p>
                  </div>
                </div>
              </div>
            </div>

            <Card className="bg-slate-900/50 border-slate-800/50">
              <CardContent className="p-8">
                <form className="space-y-6" onSubmit={handleSubmit}>
                  <div className="space-y-2">
                    <label htmlFor="fullName" className="text-sm font-medium text-slate-300">Full Name</label>
                    <input
                      type="text"
                      id="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      className="w-full bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors"
                      placeholder="John Doe"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-slate-300">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors"
                      placeholder="john@company.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="message" className="text-sm font-medium text-slate-300">Message</label>
                    <textarea
                      id="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      className="w-full bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-colors min-h-[120px] resize-y"
                      placeholder="How can we help you?"
                    ></textarea>
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-blue-500 to-violet-600 hover:from-blue-600 hover:to-violet-700 text-white py-6 rounded-xl"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <MessageSquare className="mr-2 h-5 w-5" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative rounded-3xl overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-violet-600" />
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnYtMmg0djJoMnY0aC0ydjJoLTR2LTJ6bTAtOGgtMnYtMmgydjJ6bTAgMTBoLTJ2LTJoMnYyem0tMTAtNGgtMnYtMmgydjJ6bTAgNGgtMnYtMmgydjJ6bTAtOGgtMnYtMmgydjJ6bTEwIDhoLTJ2LTJoMnYyem0xMC00aC0ydi0yaDJ2MnptMCA0aC0ydi0yaDJ2MnptMC04aC0ydi0yaDJ2MnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30" />
            <div className="relative px-8 py-16 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Ready to Transform Your Payments?
              </h2>
              <p className="text-blue-100 mb-8 max-w-xl mx-auto">
                Join thousands of businesses already processing payments with Luxkgate.
                Get started in minutes.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  size="lg"
                  className="bg-white text-blue-600 hover:bg-slate-100 hover:text-blue-700 px-8 py-6 text-lg rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-black/20 hover:-translate-y-1 border-0"
                  onClick={() => window.location.href = 'https://agent.panel.luxkgate.com/login'}
                >
                  Create Account
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  className="bg-transparent border-2 border-white/30 text-white hover:bg-white/10 hover:text-white hover:border-white/50 px-8 py-6 text-lg rounded-xl transition-all duration-300 hover:-translate-y-1"
                  onClick={() => window.location.href = '/contact'}
                >
                  Talk to Sales
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}

